"use client";

import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { SekcjaUstawien, Wiersz } from "./UstawieniaUI";

interface PasskeyUI {
  id: string;
  nazwa: string;
  dodano: string;
  uzyto: string | null;
}
interface StanLogowaniaUI {
  email: string | null;
  passkeye: PasskeyUI[];
  google: { klucz: boolean; logowanie: boolean };
}

const data = (iso: string) => new Date(iso).toLocaleDateString("pl-PL", { dateStyle: "medium" });

/** Komunikat z błędu przeglądarki przy tworzeniu passkeya (anulowanie, duplikat). */
export function bladPasskeya(e: unknown): string {
  const nazwa = (e as { name?: string } | null)?.name;
  if (nazwa === "NotAllowedError") return "Anulowano albo urządzenie odmówiło.";
  if (nazwa === "InvalidStateError") return "Ten passkey jest już dodany na tym urządzeniu.";
  if (nazwa === "SecurityError") return "Passkey działa tylko pod adresem instancji (https) albo na localhost.";
  return e instanceof Error ? e.message : "Nie udało się utworzyć passkeya.";
}

/**
 * Ustawienia → Bezpieczeństwo (ADR-0059 §10, ADR-0075): passkeye właściciela i adres konta Google.
 * Każda zmiana trafia do rejestru prób (sekcje niżej).
 */
export function SekcjaLogowania({ onZmiana }: { onZmiana?: () => void }) {
  const [stan, setStan] = useState<StanLogowaniaUI | null>(null);
  const [nazwa, setNazwa] = useState("");
  const [email, setEmail] = useState("");
  const [doUsuniecia, setDoUsuniecia] = useState<PasskeyUI | null>(null);
  const [trwa, setTrwa] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    void fetch("/api/logowanie")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: StanLogowaniaUI | null) => {
        if (!d) return;
        setStan(d);
        setEmail(d.email ?? "");
      });
  }, []);

  async function wyslij(dane: object): Promise<boolean> {
    const r = await fetch("/api/logowanie", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dane),
    }).catch(() => null);
    if (!r?.ok) {
      setBlad((await r?.text().catch(() => "")) || "Nie udało się zapisać.");
      return false;
    }
    const d = (await r.json()) as StanLogowaniaUI;
    setStan(d);
    setEmail(d.email ?? "");
    onZmiana?.();
    return true;
  }

  async function dodaj() {
    setBlad(null);
    setTrwa(true);
    try {
      const opcje = await fetch("/api/logowanie", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ akcja: "opcje-rejestracji", nazwa }),
      });
      if (!opcje.ok) throw new Error(await opcje.text());
      const odpowiedz = await startRegistration({ optionsJSON: await opcje.json() });
      if (await wyslij({ akcja: "rejestruj", nazwa, odpowiedz })) setNazwa("");
    } catch (e) {
      setBlad(bladPasskeya(e));
    } finally {
      setTrwa(false);
    }
  }

  async function usun(p: PasskeyUI) {
    setBlad(null);
    if (await wyslij({ akcja: "usun", id: p.id })) setDoUsuniecia(null);
  }

  if (!stan) return <span className="ustawienia-opis">Wczytywanie…</span>;
  const jeden = stan.passkeye.length === 1;

  return (
    <>
      <SekcjaUstawien
        nr={1}
        tytul="Passkeye"
        pomoc={
          "Logowanie odciskiem palca, twarzą albo PIN-em urządzenia, bez hasła. Passkey z menedżera haseł Google " +
          "działa na wszystkich urządzeniach z tym kontem, Windows Hello tylko na tym komputerze. Usunięcie passkeya " +
          "wylogowuje urządzenia, które się nim zalogowały. Utrata wszystkich: logowanie przez Google albo " +
          "„npm run reset-logowania” w kontenerze."
        }
      >
        {stan.passkeye.length === 0 && (
          <span className="ustawienia-opis">Brak passkeyów — logujesz się tylko przez Google.</span>
        )}
        {stan.passkeye.map((p) => (
          <div key={p.id} className="specjalizacje-apki-naglowek">
            <span>
              {p.nazwa}{" "}
              <span className="ustawienia-opis">
                dodany {data(p.dodano)} · {p.uzyto ? `ostatnio ${data(p.uzyto)}` : "jeszcze nieużyty"}
              </span>
            </span>
            {doUsuniecia?.id === p.id ? (
              <span>
                <span className="ustawienia-opis">Usunąć? Urządzenia z tym passkeyem zostaną wylogowane. </span>
                <button className="btn-maly niebezpieczny" onClick={() => void usun(p)}>
                  Usuń
                </button>
                <button className="btn-tekst" onClick={() => setDoUsuniecia(null)}>
                  Anuluj
                </button>
              </span>
            ) : (
              <button className="btn-tekst" disabled={jeden} onClick={() => setDoUsuniecia(p)}>
                Usuń
              </button>
            )}
          </div>
        ))}
        {jeden && (
          <span className="ustawienia-ostrzezenie">
            Masz tylko jeden passkey. Dodaj drugi (np. Windows Hello albo telefon), żeby nie stracić dostępu.
          </span>
        )}
        <Wiersz etykieta="Dodaj passkey" pomoc="Nazwa pomaga rozpoznać urządzenie na liście i w rejestrze prób.">
          <input
            className="ustawienia-select"
            placeholder="nazwa urządzenia, np. Telefon"
            maxLength={40}
            value={nazwa}
            onChange={(e) => setNazwa(e.target.value)}
          />
          <button className="btn-maly" disabled={trwa || !nazwa.trim()} onClick={() => void dodaj()}>
            {trwa ? "Czekam na urządzenie…" : "Dodaj"}
          </button>
        </Wiersz>
      </SekcjaUstawien>

      <SekcjaUstawien
        nr={2}
        tytul="Konto Google"
        pomoc={
          "Adres konta Google właściciela. Tylko on może się zalogować przez Google. Logowanie przez Google działa, " +
          "gdy jest też klucz Google (Integracje → Google). Zmiana adresu wylogowuje sesje założone przez Google."
        }
      >
        <Wiersz etykieta="Adres">
          <input
            type="email"
            className="ustawienia-select"
            placeholder="adres@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            className="btn-maly"
            disabled={!email.trim() || email.trim().toLowerCase() === stan.email}
            onClick={() => void wyslij({ akcja: "email", email })}
          >
            Zapisz
          </button>
          {stan.email && (
            <button className="btn-tekst" onClick={() => void wyslij({ akcja: "email", email: null })}>
              Usuń
            </button>
          )}
        </Wiersz>
        <span className="ustawienia-opis">
          Logowanie przez Google:{" "}
          {stan.google.logowanie
            ? "włączone"
            : !stan.google.klucz
              ? "wyłączone — brak klucza Google (Integracje → Google)"
              : "wyłączone — brak adresu"}
          .
        </span>
      </SekcjaUstawien>
      {blad && <span className="ustawienia-ostrzezenie">Błąd: {blad}</span>}
    </>
  );
}
